import { Button, Card, CardContent, StatusBadge } from '@app/ui';
import { FadeIn, PageTransition, StaggerContainer, StaggerItem } from '@app/ui/motion';
import { formatCurrency } from '@app/validators';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { RouteErrorComponent } from '../../../components/route-error';
import { api } from '../../../lib/api';
import { useQuota } from '../../../lib/use-feature';

const dealStatusEnum = z.enum(['DRAFT', 'ACTIVE', 'COMPLETED', 'EXPIRED', 'CANCELLED']);
type DealStatus = z.infer<typeof dealStatusEnum>;

export const Route = createFileRoute('/_authenticated/deals/')({
  validateSearch: z.object({
    status: dealStatusEnum.optional(),
    page: z.coerce.number().default(1),
  }),
  loaderDeps: ({ search }) => search,
  loader: async ({ context, deps }) => {
    const { getServerApi } = await import('../../../lib/api-server');
    const serverApi = await getServerApi();
    await context.queryClient.ensureQueryData(
      serverApi.deal.list.queryOptions({
        input: {
          status: deps.status,
          page: deps.page,
          limit: 20,
        },
      }),
    );
  },
  pendingComponent: () => (
    <div className="animate-pulse">
      <div className="h-8 bg-muted rounded w-48 mb-6" />
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-24 bg-muted rounded" />
        ))}
      </div>
    </div>
  ),
  errorComponent: ({ error }) => <RouteErrorComponent error={error} />,
  component: DealsList,
});

const STATUS_FILTERS: Array<{ label: string; value: DealStatus | undefined }> = [
  { label: 'All', value: undefined },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Draft', value: 'DRAFT' },
  { label: 'Completed', value: 'COMPLETED' },
  { label: 'Expired', value: 'EXPIRED' },
];

function DealsList() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const { data } = useSuspenseQuery(
    api.deal.list.queryOptions({
      input: {
        status: search.status,
        page: search.page,
        limit: 20,
      },
    }),
  );

  // Drives the pre-emptive "New Deal" → /pricing redirect when the user is
  // at their plan's deal cap. The server's requireWithinLimit middleware
  // remains the authoritative gate; this just avoids a wasted round-trip
  // through /deals/new.
  const { data: activeCount } = useQuery({
    ...api.deal.activeCount.queryOptions(),
    // Refetch when the page regains focus so creating a deal in a second
    // tab updates the cap state here.
    staleTime: 30 * 1000,
  });
  // Pass `activeCount` directly (not `?? 0`) so useQuota can distinguish
  // "still loading" from "really 0 active deals" and defer the cap UX until
  // both subscription + count have resolved.
  const dealQuota = useQuota('deals', activeCount);

  const { deals, total, page, totalPages } = data;

  return (
    <PageTransition>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold">My Deals</h1>
        <div className="flex gap-3">
          <Link to="/deals/import">
            <Button variant="outline" className="min-h-[44px]">
              Import Deals
            </Button>
          </Link>
          {dealQuota.atLimit ? (
            <Link to="/pricing" search={{ limit: 'deals' }}>
              <Button className="min-h-[44px]">New Deal</Button>
            </Link>
          ) : (
            <Link to="/deals/new">
              <Button className="min-h-[44px]">New Deal</Button>
            </Link>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {STATUS_FILTERS.map((filter) => {
          const isActive = search.status === filter.value;
          return (
            <Button
              key={filter.label}
              variant={isActive ? 'default' : 'outline'}
              size="sm"
              className="rounded-full"
              onClick={() =>
                navigate({
                  search: { status: filter.value, page: 1 },
                })
              }
            >
              {filter.label}
            </Button>
          );
        })}
      </div>

      {deals.length === 0 ? (
        <FadeIn>
          <Card>
            <CardContent className="py-12 text-center">
              <h3 className="text-lg font-medium mb-2">No deals yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first deal or upload a DM screenshot to get started.
              </p>
              <div className="flex gap-3 justify-center">
                <Link to="/deals/new">
                  <Button>Create Deal</Button>
                </Link>
                <Link to="/deals/import">
                  <Button variant="outline">Import Deals</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </FadeIn>
      ) : (
        <>
          <StaggerContainer className="space-y-3">
            {deals.map((deal) => (
              <StaggerItem key={deal.id}>
                <Link to="/deals/$dealId" params={{ dealId: deal.id }} className="block">
                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="py-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div className="flex items-center gap-3">
                          {deal.brandLogo ? (
                            <img
                              src={deal.brandLogo}
                              alt=""
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium">
                              {deal.brandName.charAt(0)}
                            </div>
                          )}
                          <div>
                            <p className="font-medium">{deal.title}</p>
                            <p className="text-sm text-muted-foreground">{deal.brandName}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 pl-13 sm:pl-0">
                          <div className="text-right">
                            <p className="font-medium">{formatCurrency(deal.amount)}</p>
                            <p className="text-xs text-muted-foreground">
                              {deal.paymentCount} payment{deal.paymentCount !== 1 ? 's' : ''}{' '}
                              &middot; {deal.deliverableCount} deliverable
                              {deal.deliverableCount !== 1 ? 's' : ''}
                            </p>
                          </div>
                          <StatusBadge status={deal.status} />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </StaggerItem>
            ))}
          </StaggerContainer>

          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => navigate({ search: { ...search, page: page - 1 } })}
              >
                Previous
              </Button>
              <span className="flex items-center text-sm text-muted-foreground px-3">
                Page {page} of {totalPages} ({total} deals)
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => navigate({ search: { ...search, page: page + 1 } })}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </PageTransition>
  );
}
